import axios from 'axios';
import {
  getBrokerageToken, getBrokerageRefreshToken, setBrokerageTokens, clearBrokerageTokens,
  getCarrierToken, getCarrierRefreshToken, setCarrierTokens, clearCarrierTokens
} from './auth';
import Config from '@/config';

// -------------------------------------------------------------
// Carrier Axios Instance (Hornet API)
// -------------------------------------------------------------
const CarrierHttpClient = axios.create({
  baseURL: `${Config.HORNET_URL}`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
});

// Request Interceptor: Attach Carrier Auth Token
CarrierHttpClient.interceptors.request.use((config) => {
  const token = getCarrierToken();

  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

// Response Interceptor: Capture new tokens and handle 401
let isRefreshingCarrier = false;
let failedQueueCarrier = [];

const processQueueCarrier = (error, token = null) => {
  failedQueueCarrier.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueueCarrier = [];
};

CarrierHttpClient.interceptors.response.use((response) => {
  const newAccessToken = response.headers['access-token'] || response.headers['access_token'];
  const newRefreshToken = response.headers['refresh-token'] || response.headers['refresh_token'];

  if (newAccessToken || newRefreshToken) {
    setCarrierTokens(newAccessToken, newRefreshToken);
  }
  return response;
}, async (error) => {
  const originalRequest = error.config;

  if (error.response && error.response.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    if (isRefreshingCarrier) {
      try {
        const token = await new Promise(function (resolve, reject) {
          failedQueueCarrier.push({ resolve, reject });
        });

        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return CarrierHttpClient(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    isRefreshingCarrier = true;

    const refreshToken = getCarrierRefreshToken();
    const accessToken = getCarrierToken();

    if (refreshToken && accessToken) {
      try {
        const refreshResponse = await axios.post(`${Config.HORNET_URL}admin_auth/refresh`, {
          access_token: accessToken,
          refresh_token: refreshToken
        });

        // C# endpoint returns success payload with new tokens
        if (refreshResponse.data?.access_token) {
          const newToken = refreshResponse.data.access_token;

          setCarrierTokens(newToken, refreshResponse.data.refresh_token);

          processQueueCarrier(null, newToken);
          isRefreshingCarrier = false;

          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return CarrierHttpClient(originalRequest);
        }
      } catch (err) {
        // Refresh failed, wipe session and logout
        processQueueCarrier(err, null);
        isRefreshingCarrier = false;

        clearCarrierTokens();
        window.location.hash = '/';
        return Promise.reject(err);
      }
    } else {
      // No refresh token available, force logout
      processQueueCarrier(new Error('No tokens'), null);
      isRefreshingCarrier = false;

      clearCarrierTokens();
      window.location.hash = '/';
    }
  }
  return Promise.reject(error);
});

// -------------------------------------------------------------
// Brokerage Axios Instance (Ironsides API + Hornet API etc)
// -------------------------------------------------------------
const BrokerageHttpClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
});

// Request Interceptor: Attach Brokerage Auth Token
BrokerageHttpClient.interceptors.request.use((config) => {
  const token = getBrokerageToken();

  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

// Response Interceptor: Capture new tokens and handle 401
let isRefreshingBrokerage = false;
let failedQueueBrokerage = [];

const processQueueBrokerage = (error, token = null) => {
  failedQueueBrokerage.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueueBrokerage = [];
};

BrokerageHttpClient.interceptors.response.use((response) => {
  const newAccessToken = response.headers['access-token'] || response.headers['access_token'];
  const newRefreshToken = response.headers['refresh-token'] || response.headers['refresh_token'];

  if (newAccessToken || newRefreshToken) {
    setBrokerageTokens(newAccessToken, newRefreshToken);
  }
  return response;
}, async (error) => {
  const originalRequest = error.config;

  if (error.response && error.response.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    if (isRefreshingBrokerage) {
      try {
        const token = await new Promise(function (resolve, reject) {
          failedQueueBrokerage.push({ resolve, reject });
        });

        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return BrokerageHttpClient(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    isRefreshingBrokerage = true;

    const refreshToken = getBrokerageRefreshToken();
    const accessToken = getBrokerageToken();

    if (refreshToken && accessToken) {
      try {
        const refreshResponse = await axios.post(`${Config.IRONSIDES_URL}admin_auth/refresh`, {
          access_token: accessToken,
          refresh_token: refreshToken
        });

        if (refreshResponse.data?.access_token) {
          const newToken = refreshResponse.data.access_token;

          setBrokerageTokens(newToken, refreshResponse.data.refresh_token);

          processQueueBrokerage(null, newToken);
          isRefreshingBrokerage = false;

          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return BrokerageHttpClient(originalRequest);
        }
      } catch (err) {
        processQueueBrokerage(err, null);
        isRefreshingBrokerage = false;

        clearBrokerageTokens();
        window.location.hash = '/';
        return Promise.reject(err);
      }
    } else {
      processQueueBrokerage(new Error('No tokens'), null);
      isRefreshingBrokerage = false;

      clearBrokerageTokens();
      window.location.hash = '/';
    }
  }
  return Promise.reject(error);
});

export { CarrierHttpClient, BrokerageHttpClient };